import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { environment } from '@bakerweb/baker-environment';
import { initBrowserErrorReporting, setRestAuthTokenProvider } from '@bakerweb/client-services';
import { CollapseDrawerProvider, SettingsProvider } from '@bakerweb/theme';
import { setMuiLicenseKey } from '@bakerweb/mui-license';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import { App } from './app/App';
import { sessionExpiredVar } from './state/sessionExpired';

if (!environment.msal) {
  throw new Error('baker-environment is missing msal config — cannot bootstrap auth.');
}

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: environment.msal.clientId,
    authority: environment.msal.authority,
    redirectUri: environment.msal.redirectUri,
    postLogoutRedirectUri: environment.msal.postLogoutRedirectUri
  },
  cache: {
    cacheLocation: 'sessionStorage'
  }
});

setMuiLicenseKey();

const acquireApiToken = async (): Promise<string | null> => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // No active account yet — let MsalAuthenticationTemplate handle the login redirect.
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [environment.msal.apiScope],
      account
    });
    return result.accessToken;
  } catch (err) {
    // Silent token acquisition can fail when consent is required or refresh token expired.
    // Surfacing as a 401 from the backend will trigger the session-expired dialog.
    console.warn('MSAL acquireTokenSilent failed:', err);
    return null;
  }
};

// Non-GraphQL HTTP clients (clientHttp S3 hooks, yield-book payload admin, browser
// error reporting) default to Cognito tokens — point them at MSAL instead.
setRestAuthTokenProvider(acquireApiToken);

const authLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let forwardSub: { unsubscribe: () => void } | undefined;
      let cancelled = false;

      // Resolve the token in a microtask instead of synchronously. Apollo subscribes
      // to this link during React's render phase; calling MSAL APIs like
      // acquireTokenSilent there makes MSAL synchronously emit events that update
      // MsalProvider mid-render, triggering React's "Cannot update a component while
      // rendering a different component" warning. Deferring with Promise.resolve()
      // moves that work just past the current render — the same way the Cognito link
      // defers everything behind fetchAuthSession().then(...).
      Promise.resolve().then(async () => {
        if (cancelled) return;
        const token = await acquireApiToken();
        if (cancelled) return;

        if (token) {
          operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
            headers: {
              ...headers,
              Authorization: `Bearer ${token}`
            }
          }));
        }

        forwardSub = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer)
        });
      });

      return () => {
        cancelled = true;
        forwardSub?.unsubscribe();
      };
    })
);

const errorLink = onError(({ networkError }) => {
  // The session-expired modal must fire ONLY on a genuine auth-token failure: an
  // HTTP 401 network error from the API's auth guard rejecting an expired/invalid
  // token. Business/authorization errors are returned as HTTP 200 with a GraphQL
  // error (e.g. a FORBIDDEN raised when a user lacks access to a resource); those
  // come back with no networkError and must NOT latch the modal on.
  const networkStatus = (networkError as { statusCode?: number } | null)?.statusCode;
  if (networkStatus === 401) {
    sessionExpiredVar(true);
  }
});

// Non-latching reset: any response that comes back over the wire (HTTP 200 — even
// one carrying a GraphQL business/authorization error) proves the token is still
// valid, so clear a previously-set flag in case it was set by a transient blip. A
// real 401 short-circuits before reaching here (no response is delivered), so the
// modal it raises stays up until the user signs back in.
const sessionResetLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    if (sessionExpiredVar()) {
      sessionExpiredVar(false);
    }
    return response;
  })
);

const gqlClient = new ApolloClient({
  link: ApolloLink.from([errorLink, sessionResetLink, authLink, new HttpLink({ uri: environment.graphQLUrl })]),
  cache: new InMemoryCache()
});

initBrowserErrorReporting({
  apiBaseUrl: environment.baseApiUrl,
  appName: 'baker'
});

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const root = createRoot(container);

// MSAL v3+ requires explicit initialization before any other API calls.
// Gate the React render on initialization to avoid race conditions in acquireTokenSilent.
(async () => {
  await msalInstance.initialize();

  // Process any redirect response (returning from login.microsoftonline.com).
  await msalInstance.handleRedirectPromise();

  // Restore the active account from cached sessionStorage entries.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Keep the active account in sync with future login events.
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
    }
  });

  root.render(
    <StrictMode>
      <HelmetProvider>
        <SettingsProvider
          theme={{
            themeMode: 'light',
            themeDirection: 'ltr',
            themeColor: 'blue',
            themeStretch: true,
            chartTheme: 'current'
          }}
        >
          <CollapseDrawerProvider>
            <BrowserRouter>
              <MsalProvider instance={msalInstance}>
                <ApolloProvider client={gqlClient}>
                  <App />
                </ApolloProvider>
              </MsalProvider>
            </BrowserRouter>
          </CollapseDrawerProvider>
        </SettingsProvider>
      </HelmetProvider>
    </StrictMode>
  );
})();
